/**
 * WhatsNewPage — renders the latest changelog payload from
 * GET /api/changelog/current as a grid of feature cards.
 *
 * Emoji rendering safety:
 * - We never inline emoji *characters* into the design. Icons come from
 *   lucide-react so they render identically on iOS / Android / desktop and
 *   never collide with the surrounding line-height or letter-spacing.
 * - Cards use fixed-width icon wells (h-12 w-12, flex-shrink-0) so the
 *   visual never overflows on the smallest 320px viewports.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkles,
  Package,
  Image as ImageIcon,
  Pin,
  ChevronLeft,
  PartyPopper,
} from "lucide-react";

import { useAuth } from "../../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  sparkles: Sparkles,
  package: Package,
  image: ImageIcon,
  pin: Pin,
};

export default function WhatsNewPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/changelog/current`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          if (err?.response?.status === 401) {
            navigate("/login?next=/whats-new");
            return;
          }
          setError("Couldn't load the latest update. Try again in a moment.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div
      className="min-h-screen bg-[#0d0d0f] text-white pt-20 pb-24 px-4"
      data-testid="whats-new-page"
    >
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-6 transition-colors"
          data-testid="whats-new-back"
        >
          <ChevronLeft size={16} /> Back
        </button>

        <div className="mb-8 flex items-start gap-4">
          <div
            className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shadow-[0_0_24px_rgba(249,115,22,0.35)]"
            aria-hidden="true"
          >
            <PartyPopper size={26} className="text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-title font-bold tracking-tight">
              {data?.title || "What's New"}
            </h1>
            {data?.tagline && (
              <p className="text-gray-400 text-sm sm:text-base mt-1">
                {data.tagline}
              </p>
            )}
            {data?.released_at && (
              <p className="text-gray-600 text-xs mt-2 uppercase tracking-wider">
                Released {data.released_at}
              </p>
            )}
          </div>
        </div>

        {loading && (
          <div
            className="text-gray-500 text-sm"
            data-testid="whats-new-loading"
          >
            Loading the latest…
          </div>
        )}

        {error && !loading && (
          <div
            className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-xl p-4"
            data-testid="whats-new-error"
          >
            {error}
          </div>
        )}

        {!loading && !error && data?.entries && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.entries.map((entry) => {
              const Icon = ICON_MAP[entry.icon] || Sparkles;
              return (
                <article
                  key={entry.id}
                  data-testid={`whats-new-card-${entry.id}`}
                  className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#17171f] to-[#0f0f17] p-5 hover:border-white/20 transition-colors"
                >
                  <div
                    className={`absolute -top-12 -right-12 w-36 h-36 rounded-full bg-gradient-to-br ${entry.accent} opacity-[0.18] blur-2xl pointer-events-none`}
                    aria-hidden="true"
                  />
                  <div className="relative flex flex-col gap-3">
                    <div
                      className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${entry.accent} flex items-center justify-center`}
                    >
                      <Icon size={22} className="text-white" />
                    </div>
                    <h2 className="text-lg font-bold text-white leading-snug">
                      {entry.title}
                    </h2>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      {entry.body}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate("/live")}
            className="flex-1 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-bold py-3 rounded-xl transition-all"
            data-testid="whats-new-cta-live"
          >
            Jump into Live Breaks
          </button>
          <button
            onClick={() => navigate("/locker")}
            className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-3 rounded-xl transition-all"
            data-testid="whats-new-cta-locker"
          >
            Open my Locker
          </button>
        </div>
      </div>
    </div>
  );
}
